import React, { useEffect, useState } from 'react';
import { styles } from './TrackMyOrder.css';
import Spinner from '@jbhi-fi/lanyard-ui/components/Spinner';
import { TruckMovingIllustrated } from '@jbhi-fi/lanyard-ui/components/Icons';
import { CANT_FIND_ORDER_ALERT, VALID_ORDER_NUMBER_PREFIXES } from '../../OrderTracking/constants';
import { TrackMyOrderForm } from '../../OrderTracking/TrackMyOrderForm/TrackMyOrderForm';
import Alert from '@jbhi-fi/lanyard-ui/components/Alert/Alert';
import { MarkdownBlock } from '../../MarkdownBlock';
import { RateLimitError } from '../../OrderTracking/RateLimitError';
import { SimpleBreadcrumbs, SimpleBreadcrumbsProps } from '../../SimpleBreadcrumbs/SimpleBreadcrumbs';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { useOrderTracking } from '../../hooks/useOrderTracking/useOrderTracking';
import { TrackingResults } from '../../OrderTracking/TrackingResults';
import { AlertProps, BaseContentSection, TrackingOrder } from '@jbhi-fi/apis/src/orderTrackingThemeApi/types';
import { ContentBlock } from '../../ContentBlock';

type TrackMyOrderProps = {
  environment: 'AU' | 'NZ';
  settings: { rateLimitSupportUrl?: string; isMobileCheckEnabled?: boolean };
  buildBreadcrumbs: (orderNumber?: string) => SimpleBreadcrumbsProps;
  verifiedOrderId?: string;
  setVerifiedOrderId?: (orderId?: string) => void;
  validateMobile: (
    mobileNumber: string,
    countryCode: string
  ) => Promise<{ isMobileValid: boolean; formattedMobile: string; error?: string }>;
};

export const TrackMyOrder = ({
  environment,
  settings,
  buildBreadcrumbs,
  verifiedOrderId,
  setVerifiedOrderId,
  validateMobile,
}: TrackMyOrderProps) => {
  const validOrderNumberPrefixes = VALID_ORDER_NUMBER_PREFIXES[environment];
  const [orderNumber, setOrderNumber] = useState<string | undefined>(undefined);
  const [mobileNumber, setMobileNumber] = useState<string | undefined>(undefined);
  const isMobile = !useBreakpoint('md', 'up');

  const { order, isLoading, statusCode } = useOrderTracking(
    orderNumber && mobileNumber
      ? { orderNumber: orderNumber, mobileNumber: mobileNumber }
      : { orderId: verifiedOrderId! }
  );
  const hasSubmitted = Boolean((orderNumber && mobileNumber) || verifiedOrderId);
  const isRateLimited = statusCode && [429, 498].includes(statusCode);
  const hasTrackingResults =
    order?.collections?.length || order?.deliveries?.length || order?.digitalDeliveries?.length;

  const handleSubmit = (orderNumber: string, mobileNumber?: string) => {
    setOrderNumber(orderNumber);
    mobileNumber && setMobileNumber(mobileNumber);
  };

  useEffect(() => {
    if (isLoading || !setVerifiedOrderId) return;
    setVerifiedOrderId(order?.orderId);
  }, [isLoading, order, setVerifiedOrderId]);

  return (
    <>
      {(!order && isMobile) || isLoading ? null : (
        <div className={styles.breadcrumbContainer}>
          <SimpleBreadcrumbs {...buildBreadcrumbs(order ? orderNumber || order.orderNumber || '1' : undefined)} />
        </div>
      )}
      <ContentBlock
        paddingY="large"
        paddingTop="xlarge"
        paddingX={{ mobile: 'none', tablet: 'large', desktop: 'large' }}
        className={styles.contentBlock}
      >
        <div className={styles.container}>
          {isLoading ? (
            <LoadingView />
          ) : isRateLimited ? (
            <div className={styles.dialog}>
              <RateLimitError contactUsUrl={settings?.rateLimitSupportUrl ?? ''} />
            </div>
          ) : order && hasTrackingResults ? (
            <ResultsView order={order} />
          ) : (
            <div className={styles.formContainer}>
              <FormView
                handleSubmit={handleSubmit}
                validOrderNumberPrefixes={validOrderNumberPrefixes}
                orderNumber={orderNumber}
                mobileNumber={mobileNumber}
                environment={environment}
                isMobileCheckEnabled={settings?.isMobileCheckEnabled}
                validateMobile={validateMobile}
                error={
                  hasSubmitted && ((statusCode && statusCode !== 200) || !hasTrackingResults)
                    ? CANT_FIND_ORDER_ALERT
                    : undefined /*TODO: handle other errors. cancelled order etc*/
                }
              />
            </div>
          )}
        </div>
        <div id="feedback-block" />
      </ContentBlock>
    </>
  );
};

const FormView = ({
  handleSubmit,
  validOrderNumberPrefixes,
  orderNumber,
  mobileNumber,
  error,
  environment,
  isMobileCheckEnabled,
  validateMobile,
}: {
  handleSubmit: (orderNumber: string, mobileNumber?: string) => void;
  validOrderNumberPrefixes: Array<string>;
  orderNumber?: string;
  mobileNumber?: string;
  error?: BaseContentSection & AlertProps;
  environment: string;
  isMobileCheckEnabled?: boolean;
  validateMobile: (
    mobileNumber: string,
    countryCode: string
  ) => Promise<{ isMobileValid: boolean; formattedMobile: string; error?: string }>;
}) => {
  return (
    <>
      <div className={styles.dialog}>
        <h3 className={styles.title} data-testid="heading-track-order">
          Track my order
        </h3>
        {error ? (
          <Alert
            showIcon
            {...error}
            content={<MarkdownBlock shouldLinkOpenInNewTab>{error.content || ''}</MarkdownBlock>}
          />
        ) : null}
        <TrackMyOrderForm
          onSubmit={handleSubmit}
          validOrderNumberPrefixes={validOrderNumberPrefixes}
          initialOrderNumber={orderNumber}
          initialMobileNumber={mobileNumber}
          environment={environment}
          validateMobile={validateMobile}
          isMobileCheckEnabled={isMobileCheckEnabled}
        />
        <TruckMovingIllustrated
          height="3.75rem"
          width="auto"
          className={styles.truckIcon({ isLoading: false })}
          data-chromatic="ignore"
        />
      </div>
    </>
  );
};

const LoadingView = () => {
  return (
    <>
      <div className={styles.loadingContainer}>
        <Spinner />
        Finding your order ...
      </div>
      <TruckMovingIllustrated
        height="3.75rem"
        width="auto"
        className={styles.truckIcon({ isLoading: true })}
        data-chromatic="ignore"
      />
    </>
  );
};

const ResultsView = ({ order }: { order: TrackingOrder }) => {
  return <TrackingResults order={order} />;
};
